import { useMutation } from '@apollo/client';
import axios from 'axios';
import { T, cond, equals } from 'ramda';
import React, { FC, useContext, useEffect, useState } from 'react';

import BasicText from '@ui/atoms/BasicText';
import FolxLoader from '@ui/atoms/FolxLoader';

import CenteredGridItemComponent from '@ui/molecules/CenteredGridItem';

import { ApiEndpoints } from '@utils/endpoints';

import { useAuth } from '@providers/AuthProvider';
import FlowContext from '@providers/FlowContextProvider';

import { IFlowStepProps } from '@folx-types/flow-steps';

import COMPLETE_FLOW from '@graphql/mutations/completeFlowMutation.gql';

export interface ISubmitContent {
	genericErrorMessage: string;
	pleaseWaitMessage: string;
}

interface ISubmitStepProps extends IFlowStepProps {
	paymentState?: string;
	refinedContent: ISubmitContent;
}

const Submit: FC<ISubmitStepProps> = ({ stepKey, paymentState, refinedContent }) => {
	const [error, setError] = useState(false);
	const [appointmentError, setAppointmentError] = useState(false);

	const { getAuthToken } = useAuth();

	const context = useContext(FlowContext);
	const { existingFormData, flowId, handleFormSubmission } = context;

	const [markCompleteVCFlow, { error: markCompleteError }] = useMutation(COMPLETE_FLOW);

	const { pleaseWaitMessage, genericErrorMessage } = refinedContent;

	type RetrieveStripeSession = (stripeSessionId?: string) => Promise<Record<string, unknown>>;
	const retrieveStripeSession: RetrieveStripeSession = async (stripeSessionId = '') => {
		const authorizationToken = await getAuthToken();
		const response = await axios
			.get(`${ApiEndpoints.stripe}/session?sessionId=${stripeSessionId}`, {
				headers: {
					authorizationToken,
				},
			})
			.catch((error) => {
				console.log(error);
				setError(true);
			});

		return response?.data;
	};

	type ConfirmAppointment = (cogsworthId: string) => Promise<boolean>;
	const confirmAppointment: ConfirmAppointment = async (cogsworthId: string) => {
		const authorizationToken = await getAuthToken();
		const response = await axios
			.post(
				`${ApiEndpoints.cogsworthConfirmAppointment}/${cogsworthId}`,
				{},
				{
					headers: {
						'Content-Type': 'application/json',
						Accept: 'application/json',
						authorizationToken,
					},
				}
			)
			.catch((error) => {
				console.log(error);
				setError(true);
			});

		return response?.data?.appointmentConfirmed;
	};

	const resetCogsworthAppointment = (cogsDataParam): Record<string, unknown> => {
		cogsDataParam = {
			...cogsDataParam,
			cogsworthId: null,
			cogsworthAppointmentTime: null,
			cogsworthAppointmentRescheduleLink: null,
			cogsworthAppointmentCancelLink: null,
		};
		return cogsDataParam;
	};

	const verifyPayment = async () => {
		try {
			const cogsworthId: string = (existingFormData?.cogsworthId as string) ?? '';
			if (!cogsworthId) {
				return setError(true);
			}

			const data = await retrieveStripeSession(existingFormData?.stripeSessionId);
			if (!data) {
				return setError(true);
			}

			const { active, complete, paid } = data;
			if (!active || !complete) {
				return setError(true);
			}
			if (!paid) {
				return handleFormSubmission(existingFormData, stepKey);
			}

			let dataToSubmit: any = {};
			dataToSubmit.paymentConfirmation = existingFormData?.paymentConfirmation || cogsworthId;

			const appointmentConfirmation = await confirmAppointment(cogsworthId);
			if (appointmentConfirmation) {
				dataToSubmit.appointmentConfirmation = appointmentConfirmation;
				await handleFormSubmission(dataToSubmit, stepKey);
				await markCompleteVCFlow({
					variables: {
						flowName: flowId,
					},
				});
				if (markCompleteError) {
					setError(true);
					console.error(
						`Error marking VC flow complete: ${JSON.stringify(markCompleteError.message)}`
					);
				}
			} else {
				setAppointmentError(true);
				dataToSubmit = resetCogsworthAppointment(dataToSubmit);
				await handleFormSubmission(dataToSubmit, stepKey);
			}
		} catch (err: any) {
			console.error(`Error from verifyPayment: ${JSON.stringify(err.message)}`);
		}
	};

	useEffect(() => {
		cond<string, void>([
			[
				equals('success'),
				() => {
					verifyPayment();
				},
			],
			[
				equals('cancel'),
				() => {
					setError(true);
				},
			],
			[
				T,
				() => {
					verifyPayment();
				},
			],
		])(paymentState ?? '');
	}, []);

	if (error) {
		return (
			<CenteredGridItemComponent>
				<BasicText>{genericErrorMessage}</BasicText>
			</CenteredGridItemComponent>
		);
	}

	return (
		<CenteredGridItemComponent>
			<FolxLoader message={pleaseWaitMessage} />
		</CenteredGridItemComponent>
	);
};

export default Submit;
